import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f21cf212"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "contact-selector-field" }
const _hoisted_2 = {
  key: 0,
  class: "flex gap-x-2 ml-auto h-fit"
}
const _hoisted_3 = {
  key: 0,
  class: "contact-selector-field"
}
const _hoisted_4 = {
  key: 1,
  class: "contact-selector-field"
}
const _hoisted_5 = {
  key: 2,
  class: "w-full flex items-center border-b pb-3"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "text-sm font-medium mr-2" }, "From:", -1))

import { computed, onMounted, PropType, ref, watch } from "vue"
import ContactDropdown from "@/components/profile-details/middle-panel/ContactDropdown.vue"
import GgmsLoading from "@/components/GgmsLoading.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import ContactSelectorField from "@/components/profile-details/middle-panel/ContactSelectorField.vue"
import { useConfigStore } from "@/stores/config"
import { useAgencyStore } from "@/stores/agency"
import { useIntegrationStore } from "@/stores/integration"
import { ToastService } from "@/shared/services/toast"
import { Integration } from "@/shared/models/integration"
import { Sender } from "@/shared/models/sender"
import { InboxContact } from "@/shared/models/inbox"
import { internationalPhoneFormat, getUser } from "@/shared/utils/helpers"


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsBasicEmailParts',
  props: {
    type: {
        type: String,
        default: "sms",
        validator(value: string) {
            return ["sms", "email"].includes(value)
        },
    },
    recipients: {
        type: Array as PropType<InboxContact[]>,
        default: () => [],
    },
    selectedSender: {
        type: Object as PropType<Sender>,
        default: () => ({} as Sender),
    },
    isReply: {
        type: Boolean,
        default: false,
    },
    isWorkflow: {
        type: Boolean,
        default: false,
    },
    isForward: {
        type: Boolean,
        default: false,
    },
    cc: {
        type: Array as PropType<InboxContact[]>,
        default: () => [],
    },
    bcc: {
        type: Array as PropType<InboxContact[]>,
        default: () => [],
    },
    to: {
        type: Array as PropType<string[]>,
        default: () => [],
    },
    from: {
        type: String,
        default: "",
    },
},
  emits: ["update:recipients", "update:selectedSender", "update:cc", "update:bcc"],
  setup(__props, { emit: __emit }) {

const configStore = useConfigStore()
const agencyStore = useAgencyStore()
const integrationStore = useIntegrationStore()
const toastService = new ToastService()

const props = __props

const emits = __emit

const recipients = computed({
    get() {
        return props.recipients
    },
    set(value: InboxContact[]) {
        emits("update:recipients", value)
    },
})

const localSelectedSender = computed({
    get() {
        return props.selectedSender
    },
    set(value: Sender) {
        emits("update:selectedSender", value)
    },
})

const emailCC = computed({
    get() {
        return props.cc
    },
    set(value: InboxContact[]) {
        emits("update:cc", value)
    },
})

const emailBCC = computed({
    get() {
        return props.bcc
    },
    set(value: InboxContact[]) {
        emits("update:bcc", value)
    },
})

const toField = ref()
const ccField = ref()
const bccField = ref()
const showContactSelect = ref(false)
const showCCSelect = ref(false)
const showBCCSelect = ref(false)
const isLoading = ref(false)

const integrations = computed(() => configStore.integrations as Integration[])
const senders = ref<Sender[]>([])
const isMessageType = computed(() => props.type === "sms")
const isFromVisible = computed(() => senders.value.length && (isMessageType.value || !props.isReply))
const isCCVisible = ref(false)
const isBCCVisible = ref(false)

const isCCOpen = computed(() => emailCC.value.length > 0)
const isBCCOpen = computed(() => emailBCC.value.length > 0)

function toggleCC() {
    isCCVisible.value = !isCCVisible.value
}

function toggleBCC() {
    isBCCVisible.value = !isBCCVisible.value
}

function addRecipient(recipient: InboxContact) {
    // check if the recipient is already in the list by email or phone number
    const recipientEmailOrPhone = getRecipientEmailOrPhone(recipient)
    if (recipients.value.some((rec) => getRecipientEmailOrPhone(rec) === recipientEmailOrPhone)) {
        const warningMessage = isMessageType.value
            ? "This phone number is already in the list"
            : "This email address is already in the list"
        toastService.addToast({
            message: warningMessage,
            type: "warning",
        })
        return
    }
    recipients.value.push(recipient)

    showContactSelect.value = false
}

function removeRecipient(index: number) {
    recipients.value.splice(index, 1)
}

function addCCUser(cc: InboxContact) {
    // check if the CC recipient is already in the list by email
    if (emailCC.value.some((rec) => rec.email === cc.email)) {
        toastService.addToast({
            message: "This email address is already in the list",
            type: "warning",
        })
        return
    }
    emailCC.value.push(cc)

    showCCSelect.value = false
}

function removeCCUser(index: number) {
    emailCC.value.splice(index, 1)
}

function addBCCUser(bcc: InboxContact) {
    // check if the BCC recipient is already in the list by email
    if (emailBCC.value.some((rec) => rec.email === bcc.email)) {
        toastService.addToast({
            message: "This email address is already in the list",
            type: "warning",
        })
        return
    }
    emailBCC.value.push(bcc)

    showBCCSelect.value = false
}

function removeBCCUser(index: number) {
    emailBCC.value.splice(index, 1)
}

function getRecipientEmailOrPhone(recipient: InboxContact) {
    return isMessageType.value && recipient?.phone?.number
        ? internationalPhoneFormat(recipient?.phone?.number || "")
        : getRecipientEmail(recipient)
}

function getRecipientEmail(recipient: InboxContact) {
    if (!props.isReply || props.isForward) {
        return recipient?.email || ""
    }

    if (recipient.email !== props.from) {
        return props.to?.[0]
    }

    return props.from
}

async function setIntegrations() {
    await convertIntegrationsToSenders()
    setSelectedSender()
}

function getSendgridSenderProp(propName: string) {
    return integrationStore.integrationConfig?.versions
        ?.at(-1)
        ?.sectionAnswers?.[1]?.answers?.find((answer) => answer.name === propName)?.textAnswers?.[0]
}

async function convertIntegrationsToSenders() {
    integrations.value
        .filter((integration) => integration.integrationType === "inboxEmail" && integration.isEnabled)
        .forEach((integration) => {
            senders.value.push({
                name: integration?.fromName || "",
                email: integration.fromAddress,
                integrationName: integration.integrationName,
            })
        })

    // add Sendgrid integration to the list of senders if the it's enabled
    const sendgridIntegration = integrations.value.find((integration) => integration.integrationName === "sendgrid")
    if (!sendgridIntegration) {
        return
    }
    await integrationStore.getIntegration(sendgridIntegration._id)

    const sendgridSenderEmail = getSendgridSenderProp("item_5")
    const sendgridSenderName = getSendgridSenderProp("item_6")

    if (!sendgridSenderName || !sendgridSenderEmail) {
        return
    }
    senders.value.push({
        name: sendgridSenderName,
        email: sendgridSenderEmail,
        integrationName: sendgridIntegration.integrationName,
    })
}

function setSelectedSender() {
    if (!senders.value?.length) {
        toastService.addToast({
            message: "No email address found. Please add an email address in your profile settings.",
            type: "warning",
        })
        return
    }

    const selectedIndex = senders.value.findIndex((sender) => sender.email === localSelectedSender.value?.email)

    const senderIndex = selectedIndex === -1 ? 0 : selectedIndex
    localSelectedSender.value = senders.value[senderIndex]
}

function setOutboundPhoneNumbers() {
    const agent = getUser()
    if (agent?.outboundPhoneNumber) {
        senders.value.push({
            name: agent?.fullName || "",
            phone: agent?.outboundPhoneNumber,
        })
    }

    const agencyPhone = agencyStore.agency?.agencyOutboundPhoneNumber
    if (agencyPhone) {
        senders.value.push({
            name: agencyStore.agency?.agencyName || "",
            phone: agencyPhone,
        })
    }

    if (senders.value.length) {
        localSelectedSender.value = senders.value?.[0]
        return
    }
    toastService.addToast({
        message: "No phone number found. Please add a phone number in your profile settings.",
        type: "warning",
    })
}

onMounted(async () => {
    if (isMessageType.value) {
        setOutboundPhoneNumbers()
        return
    }
    try {
        isLoading.value = true
        if (!configStore.integrations.length) {
            await configStore.getConfig()
        }
        await setIntegrations()
    } finally {
        isLoading.value = false
    }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(ContactSelectorField, {
        type: __props.type,
        fieldName: "to",
        recipients: recipients.value,
        isReply: __props.isReply,
        isForward: __props.isForward,
        from: __props.from,
        to: __props.to,
        offsetWidth: toField.value?.offsetWidth,
        isWorkflow: __props.isWorkflow,
        onAddRecipient: addRecipient,
        onRemoveRecipient: removeRecipient,
        onCloseContactSelect: _cache[0] || (_cache[0] = ($event: any) => (showContactSelect.value = false))
      }, null, 8, ["type", "recipients", "isReply", "isForward", "from", "to", "offsetWidth", "isWorkflow"]),
      (!isMessageType.value && !__props.isWorkflow)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(GgmsButton, {
              styleType: "simple",
              classes: ['py-0 px-0 text-primary-color-600'],
              onClick: toggleCC
            }, {
              default: _withCtx(() => [
                _createTextVNode("Cc")
              ]),
              _: 1
            }),
            _createVNode(GgmsButton, {
              styleType: "simple",
              classes: ['py-0 px-0 text-primary-color-600'],
              onClick: toggleBCC
            }, {
              default: _withCtx(() => [
                _createTextVNode("Bcc")
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    (isCCVisible.value || isCCOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(ContactSelectorField, {
            type: "email",
            fieldName: "cc",
            recipients: emailCC.value,
            offsetWidth: ccField.value?.offsetWidth,
            onAddRecipient: addCCUser,
            onRemoveRecipient: removeCCUser,
            onCloseContactSelect: _cache[1] || (_cache[1] = ($event: any) => (showCCSelect.value = false))
          }, null, 8, ["recipients", "offsetWidth"])
        ]))
      : _createCommentVNode("", true),
    (isBCCVisible.value || isBCCOpen.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(ContactSelectorField, {
            type: "email",
            fieldName: "bcc",
            recipients: emailBCC.value,
            offsetWidth: bccField.value?.offsetWidth,
            onAddRecipient: addBCCUser,
            onRemoveRecipient: removeBCCUser,
            onCloseContactSelect: _cache[2] || (_cache[2] = ($event: any) => (showBCCSelect.value = false))
          }, null, 8, ["recipients", "offsetWidth"])
        ]))
      : _createCommentVNode("", true),
    (isFromVisible.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _hoisted_6,
          _createVNode(ContactDropdown, {
            selectedSender: localSelectedSender.value,
            "onUpdate:selectedSender": _cache[3] || (_cache[3] = ($event: any) => ((localSelectedSender).value = $event)),
            senders: senders.value,
            type: __props.type
          }, null, 8, ["selectedSender", "senders", "type"])
        ]))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createBlock(GgmsLoading, { key: 3 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})